import type { FC, FormEvent } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield/text-field';
import SearchIcon from '@atlaskit/icon/core/search';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import type { PageCardAppearance } from '@confluence/page-card/entry-points/cardTypes.ts';
import { PageCardAppearanceToggle } from '@confluence/page-card/entry-points/PageCardAppearanceToggle.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';

import { useSelectedView } from '../../useSelectedView.ts';

import type { PillProps } from './Pills.tsx';
import { Pills } from './Pills.tsx';

const i18n = defineMessages({
	textFilterPlaceholder: {
		id: 'home.views.recent.options.placeholder',
		defaultMessage: 'Filter by title',
		description: 'Placeholder text for an input to filter pages by title',
	},
	textFilterDescription: {
		id: 'home.views.recent.options.filter.description',
		defaultMessage: 'Results will update as you type.',
		description: 'Aria description for content tree search input',
	},
	filteredResultsCount: {
		id: 'home.views.recent.filtered-results-announcement',
		defaultMessage: '{count, plural, one {# result} other {# results}} found',
		description:
			'The text is shown as an accessibility announcement on the Home page when filter options are applied, reporting the total number of matching results. The placeholder {count} is substituted with the number of results and also controls the plural form (one vs. other).',
	},
});

const optionsContainerStyles = css({
	display: 'flex',
	justifyContent: 'space-between',
	marginBottom: token('space.400'),
});

const optionsLeftStyles = css({
	display: 'flex',
});

type FilterTextProps = {
	filterText: string;
	setFilterText: (filterText: string) => void;
	pageCardDataLength?: number;
};

type AppearanceProps = {
	appearance: PageCardAppearance;
	setAppearance: (appearance: PageCardAppearance) => void;
};

type UserPreferenceProps = {
	loading: boolean;
	queryError?: ApolloErrorInterop;
	mutationError?: ApolloErrorInterop;
};

export type OptionsProps = FilterTextProps & PillProps & AppearanceProps & UserPreferenceProps;

export const Options: FC<OptionsProps> = ({
	appearance,
	setAppearance,
	filterText,
	setFilterText,
	pill,
	setPill,
	pills,
	loading,
	queryError,
	mutationError,
	pageCardDataLength,
}) => {
	const { formatMessage } = useIntl();
	const selectedView = useSelectedView();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onSearchChange = useCallback(
		(e: FormEvent<HTMLInputElement>) => {
			const text = e.currentTarget.value;
			setFilterText(text);
		},
		[setFilterText],
	);

	const onClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${selectedView}Screen`,
				action: 'clicked',
				actionSubject: 'textField',
				actionSubjectId: `${selectedView}ScreenSearchBar`,
			},
		}).fire();
	}, [createAnalyticsEvent, selectedView]);

	const placeholder = formatMessage(i18n.textFilterPlaceholder);

	return (
		<div css={optionsContainerStyles}>
			<div css={optionsLeftStyles}>
				<Textfield
					placeholder={placeholder}
					aria-label={placeholder}
					defaultValue={filterText}
					isCompact
					width="medium"
					onChange={onSearchChange}
					onClick={onClick}
					elemBeforeInput={<SearchIcon label="" spacing="spacious" />}
					aria-describedby="filterByTitleDescription"
				/>
				<VisuallyHidden id="filterByTitleDescription" role="status">
					{filterText !== ''
						? formatMessage(i18n.filteredResultsCount, { count: pageCardDataLength })
						: formatMessage(i18n.textFilterDescription)}
				</VisuallyHidden>
				<Pills pill={pill} setPill={setPill} pills={pills} />
			</div>
			<PageCardAppearanceToggle
				appearance={appearance}
				setAppearance={setAppearance}
				disabled={loading}
				source={`${selectedView}Screen`}
			/>
			{queryError && <ErrorDisplay error={queryError} />}
			{mutationError && <ErrorDisplay error={mutationError} />}
		</div>
	);
};
